import type { BaseViewConfig, ViewConfig } from 'types/contextView';
import type { UnifiedFilters } from 'types/filters';
import { concatUnifiedFilters, hasAnyFilterLane } from '@center-platform/spheres/filters';
import { isPlainObject } from './deepMergeJson';

/** Which layer supplied a resolved field. */
export type ConfigLayer = 'default' | 'view' | 'user' | 'session';

export interface ResolvedViewConfig {
  base: BaseViewConfig;
  view: ViewConfig;
  /**
   * Which layer(s) supplied each field, keyed by dotted path e.g.
   * 'base.cardDensity'. A scalar/override field resolves to the single
   * layer whose value won; a COMPOSED_RECORD_FIELDS entry (e.g.
   * 'base.levelFilters.lvl_a') resolves to every layer that contributed a
   * clause, in `default → view → user → session` order.
   */
  provenance: Record<string, ConfigLayer | ConfigLayer[]>;
}

interface LayerInput {
  base?: Partial<BaseViewConfig>;
  view?: Partial<ViewConfig>;
}

interface ResolveViewConfigLayers {
  defaults: { base: BaseViewConfig; view: ViewConfig };
  view?: LayerInput;
  user?: LayerInput;
  session?: LayerInput;
}

/**
 * `Record<levelId, …>` fields with OVERRIDE semantics — the last layer that
 * touches a given key (and, within it, a given field) wins, same as any
 * scalar field. Not consumed by the merge itself: the generic recursive
 * merge below (`mergeGenericField`) handles override semantics for EVERY
 * field uniformly, named here or not (see
 * `mergesArbitraryNestedRecordsWithoutAnAllowList` in
 * `resolveViewConfig.spec.ts`) — this list exists purely as a registry of
 * the two known override-semantics Record fields, cross-checked against
 * `COMPOSED_RECORD_FIELDS` below so the two lists can never overlap.
 */
const OVERRIDE_RECORD_FIELDS: Record<'base' | 'view', string[]> = {
  base: ['levelOverrides'],
  view: ['columnState'],
};

/**
 * `Record<levelId, UnifiedFilters>` fields where layers COMPOSE (AND —
 * per-lane concatenation, never override). `levelFilters` is the only one:
 * a view's `status in (active)` and a user's `owner = @me` on the same
 * level must both apply, not have the later layer silently discard the
 * earlier one — see `specs/per-level-context-filters.md` § Why composition,
 * not override.
 */
const COMPOSED_RECORD_FIELDS: Record<'base' | 'view', string[]> = {
  base: ['levelFilters'],
  view: [],
};

/**
 * `UnifiedFilters` fields (not `Record<levelId, …>`) where layers COMPOSE —
 * `globalFilters` is `levelFilters`'s view-wide ('*' scope) counterpart: a
 * view's `status in (active)` and a session's `owner = @me` both apply, per-lane
 * concatenation, never override. See `mergeComposedScalarFilterField`.
 */
const COMPOSED_SCALAR_FILTER_FIELDS: Record<'base' | 'view', string[]> = {
  base: ['globalFilters'],
  view: [],
};

(['base', 'view'] as const).forEach((section) => {
  const composed = new Set([...COMPOSED_RECORD_FIELDS[section], ...COMPOSED_SCALAR_FILTER_FIELDS[section]]);
  const overlap = OVERRIDE_RECORD_FIELDS[section].filter((field) => composed.has(field));
  if (overlap.length > 0) {
    throw new Error(`resolveViewConfig: field(s) listed as both override and composed: ${overlap.join(', ')}`);
  }
});

/**
 * Generically recursive merge across however many layers define a field —
 * mirrors Postgres `jsonb_deep_merge` (see `deepMergeJson`), but folded over
 * N layers instead of 2, and tracking WHICH layer supplied each leaf.
 * Recurses only while every defined layer's value at this path is a plain
 * object; otherwise the last-defined layer (in `default → view → user →
 * session` order) wins outright and is recorded in `provenance`. This is
 * what lets `levelOverrides`/`columnState` — and any future
 * `Record<levelId, …>` field, named or not — merge per key without an
 * allow-list.
 */
function mergeGenericField(
  path: string,
  entries: Array<{ layer: ConfigLayer; value: unknown }>,
  provenance: Record<string, ConfigLayer | ConfigLayer[]>
): unknown {
  const defined = entries.filter((entry) => entry.value !== undefined);
  if (defined.length === 0) return undefined;

  if (defined.every((entry) => isPlainObject(entry.value))) {
    const keys = new Set<string>();
    defined.forEach((entry) => Object.keys(entry.value as Record<string, unknown>).forEach((key) => keys.add(key)));

    const merged: Record<string, unknown> = {};
    keys.forEach((key) => {
      const subEntries = defined.map((entry) => ({
        layer: entry.layer,
        value: (entry.value as Record<string, unknown>)[key],
      }));
      const value = mergeGenericField(`${path}.${key}`, subEntries, provenance);
      if (value !== undefined) merged[key] = value;
    });
    return merged;
  }

  const winner = defined[defined.length - 1];
  provenance[path] = winner.layer;
  return winner.value;
}

/**
 * Merges a `COMPOSED_RECORD_FIELDS` entry (`levelFilters`) across however
 * many layers define it: per level id, per-lane CONCATENATION of every
 * contributing layer's `UnifiedFilters` (never override), recording EVERY
 * contributing layer in `provenance` rather than a single winner. A level
 * whose composed lanes are all empty — e.g. the only contributing layer set
 * `{}` — is pruned rather than left as a dangling empty entry.
 */
function mergeComposedLevelFilterField(
  section: 'base' | 'view',
  field: string,
  entries: Array<{ layer: ConfigLayer; value: Record<string, unknown> }>,
  provenance: Record<string, ConfigLayer | ConfigLayer[]>
): Record<string, UnifiedFilters> | undefined {
  const perLayer = entries
    .map((entry) => ({ layer: entry.layer, value: entry.value[field] as Record<string, UnifiedFilters> | undefined }))
    .filter((entry): entry is { layer: ConfigLayer; value: Record<string, UnifiedFilters> } => entry.value !== undefined);

  if (perLayer.length === 0) return undefined;

  const levelIds = new Set<string>();
  perLayer.forEach((entry) => Object.keys(entry.value).forEach((id) => levelIds.add(id)));

  const merged: Record<string, UnifiedFilters> = {};
  levelIds.forEach((levelId) => {
    let composed: UnifiedFilters | undefined;
    const contributingLayers: ConfigLayer[] = [];
    perLayer.forEach((entry) => {
      const filters = entry.value[levelId];
      if (!hasAnyFilterLane(filters)) return;
      composed = concatUnifiedFilters(composed, filters);
      contributingLayers.push(entry.layer);
    });
    if (composed && hasAnyFilterLane(composed)) {
      merged[levelId] = composed;
      provenance[`${section}.${field}.${levelId}`] = contributingLayers;
    }
  });

  return Object.keys(merged).length > 0 ? merged : undefined;
}

/**
 * Merges a `COMPOSED_SCALAR_FILTER_FIELDS` entry (`globalFilters`) across
 * however many layers define it: straight per-lane CONCATENATION of every
 * contributing layer's `UnifiedFilters` (never override) — the scalar
 * counterpart of `mergeComposedLevelFilterField`, with no per-level-id
 * indirection since the field is itself one `UnifiedFilters`, not a
 * `Record<levelId, …>`. A field whose composed lanes are all empty is pruned
 * rather than left as a dangling `{}`.
 */
function mergeComposedScalarFilterField(
  section: 'base' | 'view',
  field: string,
  entries: Array<{ layer: ConfigLayer; value: UnifiedFilters | undefined }>,
  provenance: Record<string, ConfigLayer | ConfigLayer[]>
): UnifiedFilters | undefined {
  let composed: UnifiedFilters | undefined;
  const contributingLayers: ConfigLayer[] = [];
  entries.forEach((entry) => {
    if (!hasAnyFilterLane(entry.value)) return;
    composed = concatUnifiedFilters(composed, entry.value);
    contributingLayers.push(entry.layer);
  });
  if (composed && hasAnyFilterLane(composed)) {
    provenance[`${section}.${field}`] = contributingLayers;
    return composed;
  }
  return undefined;
}

/**
 * Merges one section (`base` or `view`) across however many of the four
 * layers define it. `COMPOSED_RECORD_FIELDS[section]` fields compose
 * (`mergeComposedLevelFilterField`); every other field — scalar, array, or
 * an arbitrary `Record<levelId, …>` — merges via the generic recursive
 * merge (`mergeGenericField`), the last layer to define a leaf winning
 * override-style.
 */
function mergeSection<TSection extends object>(
  section: 'base' | 'view',
  entries: Array<{ layer: ConfigLayer; value: Partial<TSection> | undefined }>,
  provenance: Record<string, ConfigLayer | ConfigLayer[]>
): TSection {
  const defined = entries.filter(
    (entry): entry is { layer: ConfigLayer; value: Partial<TSection> } => entry.value !== undefined
  );
  const composedFields = new Set(COMPOSED_RECORD_FIELDS[section]);
  const composedScalarFilterFields = new Set(COMPOSED_SCALAR_FILTER_FIELDS[section]);

  const allKeys = new Set<string>();
  defined.forEach((entry) => Object.keys(entry.value as Record<string, unknown>).forEach((key) => allKeys.add(key)));

  const merged: Record<string, unknown> = {};

  allKeys.forEach((key) => {
    if (composedFields.has(key) || composedScalarFilterFields.has(key)) return;
    const subEntries = defined.map((entry) => ({ layer: entry.layer, value: (entry.value as Record<string, unknown>)[key] }));
    const value = mergeGenericField(`${section}.${key}`, subEntries, provenance);
    if (value !== undefined) merged[key] = value;
  });

  composedFields.forEach((field) => {
    const composed = mergeComposedLevelFilterField(
      section,
      field,
      defined as Array<{ layer: ConfigLayer; value: Record<string, unknown> }>,
      provenance
    );
    if (composed !== undefined) merged[field] = composed;
  });

  composedScalarFilterFields.forEach((field) => {
    const composed = mergeComposedScalarFilterField(
      section,
      field,
      defined.map((entry) => ({
        layer: entry.layer,
        value: (entry.value as Record<string, unknown>)[field] as UnifiedFilters | undefined,
      })),
      provenance
    );
    if (composed !== undefined) merged[field] = composed;
  });

  return merged as TSection;
}

/**
 * Composes `defaults → view → user → session` into one config plus, per
 * resolved leaf, which layer supplied it. Later layers win field-by-field —
 * an overlay that touches one field never blanks a sibling field left set
 * by an earlier layer. The incoming `ViewConfig` discriminant (`type`) is
 * carried through rather than asserted, so this never stamps `'treemap'`
 * onto an Atlas or Kanban config the way `mergeViewConfig` does.
 *
 * See `specs/view-personalization-overlay.md` § The resolver.
 */
export function resolveViewConfig(layers: ResolveViewConfigLayers): ResolvedViewConfig {
  const provenance: Record<string, ConfigLayer | ConfigLayer[]> = {};

  const base = mergeSection<BaseViewConfig>(
    'base',
    [
      { layer: 'default', value: layers.defaults.base },
      { layer: 'view', value: layers.view?.base },
      { layer: 'user', value: layers.user?.base },
      { layer: 'session', value: layers.session?.base },
    ],
    provenance
  );

  const view = mergeSection<ViewConfig>(
    'view',
    [
      { layer: 'default', value: layers.defaults.view },
      { layer: 'view', value: layers.view?.view },
      { layer: 'user', value: layers.user?.view },
      { layer: 'session', value: layers.session?.view },
    ],
    provenance
  );

  return { base, view, provenance };
}
